import { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Table, Space, Typography, Popconfirm, DatePicker, Card, List, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAllExams, createExam, updateExam, deleteExam } from '../services/examService';
import dayjs from 'dayjs';
import { useAcademicYear } from '../context/AcademicYearContext';
import useResponsive from '../hooks/useResponsive';
import { useNotification } from '../context/NotificationContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ExamManagementPage = () => {
    const { message } = useNotification();
    const { isMobile } = useResponsive();
    const { currentYear } = useAcademicYear();
    const [exams, setExams] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingExam, setEditingExam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(10);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (currentYear) {
            fetchData();
        }
    }, [currentYear]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const data = await getAllExams();
            setExams(data.map(item => ({ ...item, key: item.id })));
        } catch (error) {
            message.error('Failed to load exams.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingExam(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingExam(record);
        form.setFieldsValue({
            name: record.name,
            dates: [dayjs(record.start_date), dayjs(record.end_date)],
        });
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingExam(null);
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setConfirmLoading(true);
            const examData = {
                name: values.name,
                start_date: values.dates ? values.dates[0].format('YYYY-MM-DD') : null,
                end_date: values.dates ? values.dates[1].format('YYYY-MM-DD') : null,
            };

            if (editingExam) {
                await updateExam(editingExam.id, examData);
                message.success('Exam updated successfully!');
            } else {
                await createExam(examData);
                message.success('Exam created successfully and added to calendar!');
            }
            handleCancel();
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to save exam.');
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteExam(id);
            message.success('Exam deleted successfully!');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to delete exam.');
        }
    };

    const columns = [
        {
            title: 'Exam Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Exam Dates',
            key: 'dates',
            align: 'center',
            render: (_, record) => {
                const startDate = record.start_date ? dayjs(record.start_date).format('DD MMM, YYYY') : 'No date provided';
                const endDate = record.end_date ? dayjs(record.end_date).format('DD MMM, YYYY') : 'No date provided';
                return `${startDate} - ${endDate}`;
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm okType='default' title="Are you sure you want to delete this exam?" onConfirm={() => handleDelete(record.id)}>
                        <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const renderMobileList = () => (
        <List
            loading={loading}
            dataSource={exams}
            pagination={{
                pageSize: pageSize,
                onChange: (page, size) => setPageSize(size)
            }}
            renderItem={(item) => (
                <List.Item style={{ padding: '8px 0' }}>
                    <Card style={{ width: '100%' }} bodyStyle={{ padding: '16px' }}>
                        <List.Item.Meta
                            title={<Text strong>{item.name}</Text>}
                            description={`${dayjs(item.start_date).format('DD MMM, YYYY')} to ${dayjs(item.end_date).format('DD MMM, YYYY')}`}
                        />
                        <Space style={{ marginTop: 16, width: '100%', justifyContent: 'flex-end' }}>
                            <Button icon={<EditOutlined />} onClick={() => handleEdit(item)}>Edit</Button>
                            <Popconfirm okType='default' title="Delete this exam?" onConfirm={() => handleDelete(item.id)}>
                                <Button icon={<DeleteOutlined />} danger>Delete</Button>
                            </Popconfirm>
                        </Space>
                    </Card>
                </List.Item>
            )}
            locale={{ emptyText: <div style={{ textAlign: 'center', padding: '20px' }}><Spin spinning={loading} /><Text type="secondary">No exams found.</Text></div> }}
        />
    );

    return (
        <div style={{ margin: '0 auto' }}>
            <Card style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <Title level={2} style={{ margin: 0 }}>Exam Management</Title>
                    <Button type="default" icon={<PlusOutlined />} onClick={handleAdd}>
                        Add New Exam
                    </Button>
                </div>
            </Card>

            <Card>
                {isMobile ? renderMobileList() : (
                    <Table
                        dataSource={exams}
                        columns={columns}
                        loading={loading}
                        bordered
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            pageSize: pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100],
                            onShowSizeChange: (current, size) => setPageSize(size),
                            onChange: (page, size) => setPageSize(size),
                            responsive: true,
                            showTotal: (total) => `Total ${total} exams`
                        }}
                    />
                )}
            </Card>

            <Modal
                okType='default'
                title={editingExam ? "Edit Exam" : "Add New Exam"}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={confirmLoading}
                footer={[
                    <Button key="back" onClick={handleCancel}>Cancel</Button>,
                    <Button key="submit" type="default" loading={confirmLoading} onClick={handleOk}>
                        {editingExam ? 'Update Exam' : 'Create Exam'}
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Exam Name" rules={[{ required: true, message: "Please enter the exam name" }]}>
                        <Input placeholder="e.g., First Term Examination" />
                    </Form.Item>
                    <Form.Item name="dates" label="Exam Start & End Date" rules={[{ required: false, message: "Please select the date range" }]}>
                        <RangePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExamManagementPage;